import type { DayOfWeek, HabitFrequency } from '../interfaces'
import {
  getDailyKey,
  getHabitDay,
  getWeeklyKey,
  isScheduledDay,
  parseDailyKey,
} from './dateHelpers'

export type HabitDashboardWindow = 'week' | 'month'

export interface HabitStatsHabit {
  id: string
  frequency_type: HabitFrequency
  frequency_target: number | null
  weekly_days?: DayOfWeek[] | null
}

export interface HabitStatsLog {
  habit_id: string
  period_key: string
  skipped: boolean
}

export interface HabitDashboardMetric {
  completed: number
  expected: number
  percent: number
}

export interface HabitDashboardStats {
  week: HabitDashboardMetric
  month: HabitDashboardMetric
}

export const EMPTY_HABIT_DASHBOARD_STATS: HabitDashboardStats = {
  week: { completed: 0, expected: 0, percent: 0 },
  month: { completed: 0, expected: 0, percent: 0 },
}

interface DashboardPeriodKeys {
  week: {
    dailyKeys: string[]
    weeklyKeys: string[]
  }
  month: {
    dailyKeys: string[]
    weeklyKeys: string[]
  }
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

function getWeekStart(date: Date): Date {
  const day = date.getDay() || 7
  return addDays(date, 1 - day)
}

function getMonthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1, date.getHours())
}

function getMonthEnd(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, date.getHours())
}

function getDailyKeysBetween(
  startDate: Date,
  endDate: Date,
  dayStartTime: string,
): string[] {
  const keys: string[] = []
  let cursor = new Date(startDate)

  while (cursor <= endDate) {
    keys.push(getDailyKey(cursor, dayStartTime))
    cursor = addDays(cursor, 1)
  }

  return keys
}

function getWeeklyKeysForDailyKeys(
  dailyKeys: string[],
  dayStartTime: string,
): string[] {
  return [
    ...new Set(
      dailyKeys.map((key) =>
        getWeeklyKey(parseDailyKey(key, dayStartTime), dayStartTime),
      ),
    ),
  ]
}

export function getHabitDashboardPeriodKeys(
  selectedDate: Date,
  dayStartTime: string,
): DashboardPeriodKeys {
  const habitDay = getHabitDay(selectedDate, dayStartTime)
  const weekStart = getWeekStart(habitDay)
  const weekDailyKeys = getDailyKeysBetween(
    weekStart,
    addDays(weekStart, 6),
    dayStartTime,
  )
  const monthDailyKeys = getDailyKeysBetween(
    getMonthStart(habitDay),
    getMonthEnd(habitDay),
    dayStartTime,
  )

  return {
    week: {
      dailyKeys: weekDailyKeys,
      weeklyKeys: getWeeklyKeysForDailyKeys(weekDailyKeys, dayStartTime),
    },
    month: {
      dailyKeys: monthDailyKeys,
      weeklyKeys: getWeeklyKeysForDailyKeys(monthDailyKeys, dayStartTime),
    },
  }
}

function getLogKey(log: HabitStatsLog): string {
  return `${log.habit_id}:${log.period_key}`
}

function getMetricPercent(completed: number, expected: number): number {
  if (expected === 0) return 0
  return Math.round((completed / expected) * 100)
}

function getExpectedCompletions(
  habits: HabitStatsHabit[],
  dailyKeys: string[],
  weeklyKeys: string[],
  dayStartTime = '00:00',
): number {
  return habits.reduce((total, habit) => {
    if (habit.frequency_type === 'daily') return total + dailyKeys.length
    if (habit.frequency_type === 'weekly') return total + weeklyKeys.length
    if (habit.frequency_type === 'weekly_days') {
      if (!habit.weekly_days || habit.weekly_days.length === 0)
        return total + dailyKeys.length
      const scheduledCount = dailyKeys.filter((key) =>
        isScheduledDay(
          parseDailyKey(key, dayStartTime),
          habit.weekly_days,
          dayStartTime,
        ),
      ).length
      return total + scheduledCount
    }

    return total + weeklyKeys.length * Math.max(1, habit.frequency_target ?? 1)
  }, 0)
}

function getCompletedCountForHabit(
  habit: HabitStatsHabit,
  logs: HabitStatsLog[],
  dailyKeySet: Set<string>,
  weeklyKeys: string[],
  dayStartTime: string,
): number {
  const periodCounts = new Map<string, number>()

  for (const log of logs) {
    if (log.habit_id !== habit.id || log.skipped) continue

    if (habit.frequency_type === 'weekly') {
      if (weeklyKeys.includes(log.period_key)) {
        periodCounts.set(log.period_key, 1)
      }
      continue
    }

    if (!dailyKeySet.has(log.period_key)) continue

    if (
      habit.frequency_type === 'daily' ||
      habit.frequency_type === 'weekly_days'
    ) {
      periodCounts.set(log.period_key, 1)
      continue
    }

    const weeklyKey = getWeeklyKey(
      parseDailyKey(log.period_key, dayStartTime),
      dayStartTime,
    )
    periodCounts.set(weeklyKey, (periodCounts.get(weeklyKey) ?? 0) + 1)
  }

  if (habit.frequency_type !== 'x_per_week') {
    return periodCounts.size
  }

  const target = Math.max(1, habit.frequency_target ?? 1)

  return weeklyKeys.reduce(
    (total, weeklyKey) =>
      total + Math.min(periodCounts.get(weeklyKey) ?? 0, target),
    0,
  )
}

function calculateMetric(
  habits: HabitStatsHabit[],
  logs: HabitStatsLog[],
  dailyKeys: string[],
  weeklyKeys: string[],
  dayStartTime: string,
): HabitDashboardMetric {
  const dailyKeySet = new Set(dailyKeys)
  const expected = getExpectedCompletions(
    habits,
    dailyKeys,
    weeklyKeys,
    dayStartTime,
  )
  const dedupedLogs = [
    ...new Map(logs.map((log) => [getLogKey(log), log])).values(),
  ]
  const completed = habits.reduce(
    (total, habit) =>
      total +
      getCompletedCountForHabit(
        habit,
        dedupedLogs,
        dailyKeySet,
        weeklyKeys,
        dayStartTime,
      ),
    0,
  )

  return {
    completed,
    expected,
    percent: getMetricPercent(completed, expected),
  }
}

export function calculateHabitDashboardStats(
  habits: HabitStatsHabit[],
  logs: HabitStatsLog[],
  selectedDate: Date,
  dayStartTime: string,
): HabitDashboardStats {
  const periodKeys = getHabitDashboardPeriodKeys(selectedDate, dayStartTime)

  return {
    week: calculateMetric(
      habits,
      logs,
      periodKeys.week.dailyKeys,
      periodKeys.week.weeklyKeys,
      dayStartTime,
    ),
    month: calculateMetric(
      habits,
      logs,
      periodKeys.month.dailyKeys,
      periodKeys.month.weeklyKeys,
      dayStartTime,
    ),
  }
}
